// 蓝宝在线咨询抽屉：对接真实客服会话系统（/api/ai/customer/public/sessions，与官网同一套）
// 数据层 useCustomerChat（凭据持久化 + 3s 轮询 + 转人工 + 会话状态机）；弹层样式对齐官网 AIButton
// 桌面=右侧滑出(420px)，移动端=底部弹层；买家端 audience=BUYER（已登录不做 lead 留资表单）
// C1 登录态：打开抽屉自动认领本机访客会话（claim）；「新会话」登录走归属创建、未登录本地重启
import { Box, Drawer, Typography, TextField, IconButton, Avatar, Button, useMediaQuery, useTheme } from '@mui/material';
import { useState, useRef, useEffect } from 'react';
import { Send, Close, FiberNew } from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { usePrefs } from '@/store/BuyerPrefsContext';
import { useBuyerAuth } from '@/store/BuyerAuthContext';
import { useCustomerChat } from '@/hooks/useCustomerChat';
import { chatText, savedChat, type ChatMessage } from '@/lib/customerChat';
import { TOKEN_KEY } from '@/api/request';
import lanBaoLogo from '@/assets/logo.jpg';

import { apiLang, uiLocale } from '@/i18n';
interface LanBaoDrawerProps {
  open: boolean;
  onClose: () => void;
}

export default function LanBaoDrawer({ open, onClose }: LanBaoDrawerProps) {
  const { lang, t } = usePrefs();
  const { isAuthenticated } = useBuyerAuth();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const text = chatText(lang);
  // 客服后端（AI 回复/人工翻译）只支持 zh/en：韩文界面按英文会话，界面文案仍是韩文
  const chat = useCustomerChat(open, apiLang(lang), 'BUYER');
  const [input, setInput] = useState('');
  const [switching, setSwitching] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);
  const claimedRef = useRef(false);

  const bearer = (): string => {
    try { return localStorage.getItem(TOKEN_KEY) || ''; } catch { return ''; }
  };

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chat.messages, chat.session?.thinking, open]);

  // 登录态打开抽屉：自动认领本机访客会话（幂等；每次打开最多尝试一次）
  useEffect(() => {
    if (!open || !isAuthenticated || claimedRef.current) return;
    const auth = savedChat('BUYER');
    if (!auth) return;
    claimedRef.current = true;
    void chat.claimGuest(bearer());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isAuthenticated]);

  const send = async () => {
    const draft = input.trim();
    if (!draft || chat.busy || chat.session?.thinking) return;
    if (await chat.send(draft)) setInput('');
  };

  // 新会话：登录=创建归属买家的会话（服务端多会话）；未登录=本地重启匿名会话
  const startNew = async () => {
    if (switching) return;
    if (isAuthenticated) {
      setSwitching(true);
      await chat.newSessionSignedIn(bearer());
      setSwitching(false);
    } else {
      chat.restart();
    }
  };

  const closed = chat.session?.status === 'CLOSED' || chat.error === 'expired';

  // 非买家消息的角色标签：AI / 人工客服 / 系统（Bluplanet）
  const roleLabel = (role: ChatMessage['role']) =>
    role === 'STAFF' ? text.staff : role === 'AI' ? 'AI' : 'Bluplanet';

  return (
    <Drawer
      anchor={isDesktop ? 'right' : 'bottom'}
      open={open}
      onClose={onClose}
      slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0,0,0,0.2)', backdropFilter: 'blur(2px)' } } }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          // 官网同款：桌面右侧 420px 全高，移动端底部 70vh 弹层
          width: isDesktop ? 420 : { xs: '100%', sm: 480 },
          height: isDesktop ? '100vh' : '70vh',
          mx: isDesktop ? 0 : 'auto',
          bgcolor: '#F0FDF4',
        }}
      >
        {/* 头部：官网同款深绿；副标题=真实会话状态（AI 接待/等待人工/人工接待中/已结束）+ 新会话入口 */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 1.5,
            bgcolor: '#064E3B',
            color: '#FFFFFF',
            flexShrink: 0,
          }}
        >
          <Avatar src={lanBaoLogo} alt="Bluplanet" sx={{ width: 28, height: 28 }} />
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>{t('nav.ai')}</Typography>
            <Typography sx={{ fontSize: '11px', color: '#A7F3D0' }} role="status">
              {text[chat.session?.status || 'AI']}
            </Typography>
          </Box>
          <IconButton
            size="small"
            aria-label={text.restart}
            onClick={() => void startNew()}
            disabled={switching}
            sx={{ color: '#D1FAE5', '&:hover': { color: '#FFFFFF' } }}
          >
            <FiberNew fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={onClose} sx={{ color: '#FFFFFF' }}>
            <Close fontSize="small" />
          </IconButton>
        </Box>

        {/* 消息区：浅绿机身 */}
        <Box sx={{ flex: 1, overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, bgcolor: '#F0FDF4' }}>
          {!chat.messages.length && (
            <Typography sx={{ fontSize: '13px', color: '#064E3B', lineHeight: 1.7, px: 0.5 }}>
              {text.welcome}
            </Typography>
          )}
          {chat.messages.map((m) => (
            <Box
              key={m.seq}
              sx={{ display: 'flex', gap: 1, flexDirection: m.role === 'CUSTOMER' ? 'row-reverse' : 'row', alignItems: 'flex-start' }}
            >
              {m.role === 'CUSTOMER' ? (
                <Avatar sx={{ width: 28, height: 28, bgcolor: '#064E3B', fontSize: 14 }}>{t('comp.chatCustomer')}</Avatar>
              ) : m.role === 'STAFF' ? (
                <Avatar sx={{ width: 28, height: 28, bgcolor: '#059669', fontSize: 12 }}>{t('comp.chatStaff')}</Avatar>
              ) : (
                <Avatar src={lanBaoLogo} alt="Bluplanet" sx={{ width: 28, height: 28 }} />
              )}
              <Box
                sx={{
                  maxWidth: '85%',
                  px: 1.5,
                  py: 1.25,
                  borderRadius: '16px',
                  fontSize: '13px',
                  lineHeight: 1.6,
                  whiteSpace: m.role === 'CUSTOMER' ? 'pre-wrap' : 'normal',
                  wordBreak: 'break-word',
                  // 官网同款气泡：对方=白底绿边框左下角，买家=品牌绿白字右下角
                  bgcolor: m.role === 'CUSTOMER' ? '#10B981' : '#FFFFFF',
                  color: m.role === 'CUSTOMER' ? '#FFFFFF' : '#064E3B',
                  border: m.role === 'CUSTOMER' ? 'none' : '1px solid #D5E3DB',
                  borderBottomRightRadius: m.role === 'CUSTOMER' ? '4px' : '16px',
                  borderBottomLeftRadius: m.role !== 'CUSTOMER' ? '4px' : '16px',
                }}
              >
                {m.role !== 'CUSTOMER' && (
                  <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#059669', mb: 0.25 }}>
                    {roleLabel(m.role)}
                  </Typography>
                )}
                {m.role === 'CUSTOMER' ? (
                  m.content
                ) : (
                  <Box
                    sx={{
                      '& > :first-child': { mt: 0 },
                      '& > :last-child': { mb: 0 },
                      '& p': { my: 0, mb: 1 },
                      '& ul, & ol': { pl: 2.5, my: 0, mb: 1 },
                      '& li': { mb: 0.25 },
                      '& blockquote': { borderLeft: '2px solid #A7F3D0', pl: 1, ml: 0, color: '#52645C' },
                      '& pre': { overflowX: 'auto', p: 1, borderRadius: 1, bgcolor: '#F0FDF4' },
                      '& code': { px: 0.4, borderRadius: 0.5, bgcolor: '#F0FDF4', fontSize: '0.92em' },
                      '& a': { color: '#047857' },
                      '& table': { display: 'block', overflowX: 'auto', borderCollapse: 'collapse' },
                      '& th, & td': { border: '1px solid #D5E3DB', px: 0.75, py: 0.25 },
                      '& strong': { fontWeight: 700 },
                    }}
                  >
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown>
                  </Box>
                )}
                <Typography sx={{ fontSize: '10px', opacity: 0.6, mt: 0.5 }}>
                  {new Date(m.createdAt).toLocaleTimeString(uiLocale(lang), { hour: '2-digit', minute: '2-digit' })}
                </Typography>
              </Box>
            </Box>
          ))}
          {chat.session?.thinking && (
            <Typography role="status" sx={{ fontSize: '11px', color: '#52645C', px: 0.5 }}>
              {text.thinking}
            </Typography>
          )}
          <div ref={endRef} />
        </Box>

        {/* 输入区：官网同款（错误横幅 / 已结束→新会话 / 输入+转人工） */}
        <Box sx={{ p: 1.5, borderTop: '1px solid #D5E3DB', bgcolor: '#FFFFFF', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {chat.error && (
            <Typography role="alert" sx={{ fontSize: '11px', color: '#B91C1C' }}>
              {text[chat.error]}
            </Typography>
          )}
          {closed ? (
            <Button
              disabled={chat.busy || switching}
              onClick={() => void startNew()}
              sx={{ textTransform: 'none', fontSize: '13px', color: '#07835D', alignSelf: 'flex-start', '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' } }}
            >
              {text.restart}
            </Button>
          ) : (
            <>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={text.placeholder}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey && !(e as unknown as { nativeEvent?: { isComposing?: boolean } }).nativeEvent?.isComposing) {
                      e.preventDefault();
                      void send();
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: '#F0FDF4',
                      '& fieldset': { borderColor: '#D5E3DB' },
                      '&:hover fieldset': { borderColor: '#A7F3D0' },
                    },
                  }}
                />
                <IconButton
                  aria-label={text.send}
                  onClick={() => void send()}
                  disabled={!input.trim() || chat.busy || Boolean(chat.session?.thinking)}
                  sx={{ bgcolor: '#10B981', color: '#FFFFFF', '&:hover': { bgcolor: '#059669' }, '&.Mui-disabled': { bgcolor: '#D1FAE5', color: '#FFFFFF' } }}
                >
                  <Send fontSize="small" />
                </IconButton>
              </Box>
              <Button
                disabled={chat.busy || chat.session?.status === 'HUMAN' || chat.session?.status === 'WAITING'}
                onClick={() => void chat.human()}
                sx={{ textTransform: 'none', fontSize: '11px', color: '#07835D', alignSelf: 'flex-start', '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' }, '&.Mui-disabled': { color: '#9DBBAE' } }}
              >
                {text.human}
              </Button>
            </>
          )}
        </Box>
      </Box>
    </Drawer>
  );
}
